import { clearMtLeavesCacheForScope, syncMtLeavesWithCache } from './mtLeavesSyncCache';
import {
  type AccountReconstructionDepositRequest,
  type AccountReconstructionDepositResponse,
  type AccountReconstructionWithdrawalRequest,
  type AccountReconstructionWithdrawalResponse,
  type DepositsByLabelResponse,
  type GlobalEventsResponse,
  type MtLeavesResponse,
  type MtRootResponse,
  type PoolEventsResponse,
  type PoolStatisticsResponse,
  type PoolStatsResponse,
} from './types';

const DEFAULT_POOLS_STATS_CHAIN_ID = 143;

class HttpRequestError extends Error {
  public readonly status: number;
  public readonly code?: string;
  public readonly body?: unknown;

  public constructor(
    message: string,
    status: number,
    code?: string,
    body?: unknown,
  ) {
    super(message);
    this.name = 'HttpRequestError';
    this.status = status;
    this.code = code;
    this.body = body;
  }
}

const parseErrorResponse = async (
  response: Response,
): Promise<{ message: string; code?: string; body?: unknown }> => {
  let parsedBody: unknown;

  try {
    parsedBody = await response.clone().json();
  } catch {
    try {
      parsedBody = await response.text();
    } catch {
      parsedBody = undefined;
    }
  }

  const bodyObject =
    typeof parsedBody === 'object' && parsedBody !== null ? parsedBody : null;
  const code =
    bodyObject && 'error' in bodyObject && typeof bodyObject.error === 'string'
      ? bodyObject.error
      : undefined;
  const fallbackText =
    typeof parsedBody === 'string' && parsedBody.length > 0
      ? parsedBody
      : response.statusText;
  const message = code
    ? `Request failed: ${code}`
    : `Request failed: ${fallbackText || response.status}`;

  return { message, code, body: parsedBody };
};

const fetchWithHeaders = async <T>(
  url: string,
  headers?: Record<string, string>,
  init?: Omit<RequestInit, 'headers'>,
): Promise<T> => {
  const response = await fetch(url, {
    ...init,
    headers: {
      ...headers,
    },
  });

  if (!response.ok) {
    const parsed = await parseErrorResponse(response);
    throw new HttpRequestError(
      parsed.message,
      response.status,
      parsed.code,
      parsed.body,
    );
  }

  return response.json();
};

const postWithBody = async <T>(
  url: string,
  body: unknown,
  headers?: Record<string, string>,
): Promise<T> => {
  const response = await fetch(url, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...headers,
    },
    body: JSON.stringify(body),
  });

  if (!response.ok) {
    const parsed = await parseErrorResponse(response);
    throw new HttpRequestError(
      parsed.message,
      response.status,
      parsed.code,
      parsed.body,
    );
  }

  return response.json();
};

export const aspClient = {
  fetchPoolStats: (aspUrl: string, chainId: number | 'all') => {
    const resolvedChainId =
      chainId === 'all' ? DEFAULT_POOLS_STATS_CHAIN_ID : chainId;

    return fetchWithHeaders<PoolStatsResponse>(
      `${aspUrl}/${resolvedChainId}/public/pools-stats?chainid=${resolvedChainId}`,
      undefined,
      {
        cache: 'no-store',
      },
    );
  },

  fetchGlobalEvents: (aspUrl: string, page = 1, perPage = 6) => {
    return fetchWithHeaders<GlobalEventsResponse>(
      `${aspUrl}/global/public/events?page=${page}&perPage=${perPage}`,
      undefined,
      {
        cache: 'no-store',
      },
    );
  },

  fetchPoolEvents: (
    aspUrl: string,
    chainId: number,
    scope: string,
    page = 1,
    perPage = 20,
  ) => {
    return fetchWithHeaders<PoolEventsResponse>(
      `${aspUrl}/${chainId}/public/events?page=${page}&perPage=${perPage}`,
      {
        'X-Pool-Scope': scope,
      },
      {
        cache: 'no-store',
      },
    );
  },

  fetchPoolStatistics: (aspUrl: string, chainId: number, scope: string) => {
    return fetchWithHeaders<PoolStatisticsResponse>(
      `${aspUrl}/${chainId}/public/pool-statistics`,
      {
        'X-Pool-Scope': scope,
      },
      {
        cache: 'no-store',
      },
    );
  },

  fetchDepositsByLabel: (
    aspUrl: string,
    chainId: number,
    scope: string,
    labels: string[],
  ) =>
    fetchWithHeaders<DepositsByLabelResponse>(
      `${aspUrl}/${chainId}/public/deposits-by-label`,
      {
        'X-Pool-Scope': scope,
        'X-Labels': labels.join(','),
      },
      {
        cache: 'no-store',
      },
    ),

  fetchAccountReconstructionDeposits: (
    aspUrl: string,
    chainId: number,
    scope: string,
    request: AccountReconstructionDepositRequest,
  ) =>
    postWithBody<AccountReconstructionDepositResponse>(
      `${aspUrl}/${chainId}/public/account-reconstruction/deposits`,
      request,
      { 'X-Pool-Scope': scope },
    ),

  fetchAccountReconstructionWithdrawals: (
    aspUrl: string,
    chainId: number,
    scope: string,
    request: AccountReconstructionWithdrawalRequest,
  ) =>
    postWithBody<AccountReconstructionWithdrawalResponse>(
      `${aspUrl}/${chainId}/public/account-reconstruction/withdrawals`,
      request,
      { 'X-Pool-Scope': scope },
    ),

  fetchMtRoots: (aspUrl: string, chainId: number, scope?: string) =>
    fetchWithHeaders<MtRootResponse>(
      `${aspUrl}/${chainId}/public/mt-roots`,
      scope
        ? {
            'X-Pool-Scope': scope,
          }
        : undefined,
      {
        cache: 'no-store',
      },
    ),

  fetchMtLeaves: (aspUrl: string, chainId: number, scope: string, limit = 2000) =>
    syncMtLeavesWithCache({
      chainId,
      scope,
      limit,
      fetchPage: ({ cursor, limit: pageLimit }) => {
        const searchParams = new URLSearchParams();
        searchParams.set('limit', String(pageLimit));
        if (cursor && cursor.length > 0) {
          searchParams.set('cursor', cursor);
        }

        return fetchWithHeaders<MtLeavesResponse>(
          `${aspUrl}/${chainId}/public/mt-leaves?${searchParams.toString()}`,
          {
            'X-Pool-Scope': scope,
          },
          {
            cache: 'no-store',
          },
        );
      },
    }),

  clearMtLeavesCache: async (chainId: number, scope: string) => {
    await clearMtLeavesCacheForScope(chainId, scope);
  },
};
